import React, { useState } from "react"
import { useFlags } from "flagsmith/react"

import DeliveryOptionCard from "@/modules/shared/components/DeliveryOptionsCard"

import { IQuote } from "@/types/quotes.types"
import { ProvidersType } from "@/types/providers.types"
import { isEmpty } from "lodash"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight, faChevronUp } from "@fortawesome/free-solid-svg-icons"
import { useMediaQuery } from "usehooks-ts"

interface IDeliveryOptionListProps {
  quotes: IQuote[]
  selectedQuote: IQuote | null | undefined
  onSelect: (quote: IQuote | null) => Promise<void>
  compact?: boolean
  selectionDisabled?: boolean
}

// Enable provider
const DeliveryOptionsList: React.FC<IDeliveryOptionListProps> = ({
  quotes,
  selectedQuote,
  onSelect,
  compact,
  selectionDisabled,
}) => {
  const { t } = useTranslation()

  const isDesktop = useMediaQuery("(min-width: 624px)")

  const flags = useFlags([
    "show_grab",
    "show_lalamove",
    "show_pandago",
    "show_zeek",
    "show_pickupp",
    "show_qxpress",
    "show_ifood",
    "show_teleport",
    "show_gogox",
    "show_borzo",
    "show_ninjavan",
    "show_jnt",
    "show_tada",
    "show_rappi",
    "show_milkrun",
    "show_kosmo",
    "show_uber",
    "show_failed_quotes",
  ])

  const providerFlags: Record<ProvidersType, boolean> = {
    [ProvidersType.GRAB]: flags.show_grab.enabled,
    [ProvidersType.LALAMOVE]: flags.show_lalamove.enabled,
    [ProvidersType.PANDAGO]: flags.show_pandago.enabled,
    [ProvidersType.ZEEK]: flags.show_zeek.enabled,
    [ProvidersType.PICKUPP]: flags.show_pickupp.enabled,
    [ProvidersType.QXPRESS]: flags.show_qxpress.enabled,
    [ProvidersType.IFOOD]: flags.show_ifood.enabled,
    [ProvidersType.TELEPORT]: flags.show_teleport.enabled,
    [ProvidersType.GOGOX]: flags.show_gogox.enabled,
    [ProvidersType.BORZO]: flags.show_borzo.enabled,
    [ProvidersType.NINJAVAN]: flags.show_ninjavan.enabled,
    [ProvidersType.JNT]: flags.show_jnt.enabled,
    [ProvidersType.TADA]: flags.show_tada.enabled,
    [ProvidersType.RAPPI]: flags.show_rappi.enabled,
    [ProvidersType.MILKRUN]: flags.show_milkrun.enabled,
    [ProvidersType.KOSMO]: flags.show_kosmo.enabled,
    [ProvidersType.GOGET]: true,
    [ProvidersType.UBER]: flags.show_uber.enabled,
    [ProvidersType.OWN_FLEET]: true,
    [ProvidersType.KOSMO_NETWORK]: true,
  }

  const isProviderEnabled = (provider: ProvidersType) => {
    return !!providerFlags[provider]
  }
  const successfulQuotes = quotes.filter((quote) => !quote.error)
  const failedQuotes = quotes.filter((quote) => quote.error && isProviderEnabled(quote.provider))
  const [isExpanded, setIsExpanded] = useState(isEmpty(successfulQuotes))

  return (
    <div
      id="delivery-options-list"
      className={`hideScroll w-full ${compact ? "max-h-[300px] overflow-auto" : ""} ${
        !isDesktop ? "h-full overflow-y-visible max-h-[100%]" : ""
      }`}
    >
      {isEmpty(successfulQuotes) ? (
        <div className="flex w-full justify-center mt-2">
          <p className="font-semibold">{t("create-order.DeliveryOptionList.noQuotesFound")}</p>
        </div>
      ) : (
        successfulQuotes.map(
          (quote, index) =>
            isProviderEnabled(quote.provider) && (
              <>
                <DeliveryOptionCard
                  key={index}
                  index={index}
                  onSelect={onSelect}
                  quote={quote}
                  selectedQuote={selectedQuote}
                  compact={compact}
                  selectionDisabled={selectionDisabled}
                />
              </>
            )
        )
      )}
      {!isEmpty(failedQuotes) && flags.show_failed_quotes.enabled && (
        <div className="flex flex-col mt-4">
          <div
            className="flex gap-x-2 items-center cursor-pointer mb-2"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <p>
              {t("common.DeliveryOptions.seeUnavailableQuotes", {
                count: failedQuotes.length,
                plural: failedQuotes.length > 1 ? "s" : "",
              })}
            </p>
            <FontAwesomeIcon
              id="open-failed-quotes-list"
              className="flex-shrink-0 w-3 h-3 text-sm"
              icon={isExpanded ? faChevronUp : faChevronRight}
            />
          </div>
          {isExpanded &&
            failedQuotes.map((quote, index) => (
              <DeliveryOptionCard
                key={index}
                index={index}
                onSelect={onSelect}
                quote={quote}
                selectedQuote={selectedQuote}
                compact={compact}
                isError={true}
                selectionDisabled={selectionDisabled}
              />
            ))}
        </div>
      )}
    </div>
  )
}

export default DeliveryOptionsList
